import { useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { useHistory } from "react-router-dom";
import i18n from "i18next";

import { getProjectSettings } from "../../modules/settings";
import { siteMode } from "../../cores/siteMode";

import { EntryLoader } from "../../components/loader";
import { LayoutForNoting } from "../../components/layout";
import TokenExpiredModal from "../../components/modals/TokenExpired";
import menuService from "@services/menuService";
import useSettings from "@hooks/useSettings";
import { ENTRY_SCREEN, ENTRY_SCREEN_V2 } from "@routes/admins/settings/constants";
import { loadCustomMenus, resetCustomMenus } from "@modules/custom-menus";
import AccessDenied from "@routes/errors/Error403";
import { CUSTOM_MENU_DOMAIN, CUSTOM_MENU_TYPE } from "@constants";

function EntryConsole() {
    const history = useHistory();
    const dispatch = useDispatch();
    const isCms = siteMode.get() === "CMS";

    const { updateLocaleLanguage, updateGeneralSettings, i18nLng } = useSettings({ type: "General" });

    const settings = useSelector(({ settings }) => settings);
    const global = useSelector(({ global }) => global);

    const [siteError, setSiteError] = useState(false);
    const { stage, project, customMenus } = useSelector(
        (state) => ({
            stage: state.stage,
            project: state.project,
            customMenus: state.customMenus,
        }),
        shallowEqual,
    );

    if (!project.id) history.push(`${menuService.paths.spaces()}`);

    function getSiteMode(userProject) {
        const currentSiteMode = siteMode.get();
        const isConsole = currentSiteMode === "CONSOLE";
        const siteAccess = userProject.siteAccess;
        const hasConsoleAccess = siteAccess.indexOf("CONSOLE") !== -1;
        const hasCmsAccess = siteAccess.indexOf("CMS") !== -1;
        const targetSite =
            (isConsole && hasConsoleAccess) || (!isConsole && !hasCmsAccess && hasConsoleAccess)
                ? "CONSOLE"
                : hasCmsAccess
                ? "CMS"
                : null;
        return targetSite;
    }

    function redirect(redirectPath) {
        // 현재 버전(stage.version)? 이 더 높으면 해당 영역 수행
        if (stage.user && stage.user.projects) {
            // project 가 있어야지만 해당 페이지 접근 가능
            const userProject = stage.user.projects.filter((p) => p.id === project.id)[0];
            if (!userProject) {
                setSiteError(true);
                return;
            }
            const targetSite = getSiteMode(userProject);
            switch (targetSite) {
                case "CONSOLE": {
                    window.location.href = `${window.location.origin}${redirectPath}`;
                    break;
                }
                case "CMS": {
                    window.location.href = `${window.location.origin}/library${redirectPath}`;
                    break;
                }
                default: {
                    // case null
                    setSiteError(true);
                    break;
                }
            }
        }
    }

    const redirectCustomMenus = useCallback(() => {
        const findTopLevelMenu = (menus) => {
            for (let menu of menus) {
                if (menu.domain) return menu;
                if (menu.subMenus && menu.subMenus.length > 0) {
                    const result = findTopLevelMenu(menu.subMenus);
                    if (result) return result;
                }
            }
            return undefined;
        };

        const parseDetailTypeMenu = (menu) => {
            if (menu?.type !== CUSTOM_MENU_TYPE.DETAIL) return "";

            if (menu?.domain === CUSTOM_MENU_DOMAIN.COLLECTIONS) {
                return `/${menu?.searchParams.collections?.[0]?.id}`;
            }
            return "";
        };

        const defaultMenu =
            customMenus.data?.[0]?.subMenus?.length > 0 ? customMenus.data[0].subMenus[0] : customMenus.data?.[0];

        const topLevelMenu = findTopLevelMenu(customMenus.data) ?? defaultMenu;
        const path = `/spaces/${stage.id}/projects/${project.id}/customMenuSets/${customMenus.setId}/${
            topLevelMenu?.id
        }${parseDetailTypeMenu(topLevelMenu)}`;
        redirect(path);
    }, [customMenus]);

    const redirectDefaultMenus = async (settings) => {
        const entryScreenValue = JSON.parse(settings?.General?.entryScreen?.value || "{}");
        const redirectPath =
            entryScreenValue?.path === ENTRY_SCREEN.CREATE_ASSETS.values?.[0]?.path
                ? ENTRY_SCREEN_V2.CREATE_ASSETS.values?.[0]?.path
                : entryScreenValue?.path;

        redirect(
            redirectPath
                ? `/spaces/${stage.id}/projects/${project.id}${redirectPath}`
                : `/spaces/${stage.id}/projects/${project.id}/dashboard`,
        );
    };

    const makeMenus = useCallback(async () => {
        try {
            const settings = await dispatch(
                getProjectSettings({
                    apiEndpoint: stage.endpoint,
                    stageId: stage.id,
                    projectId: project.id,
                }),
            );

            const isCustomMenu = settings.MenuName?.selectMenu?.value === "custom";
            // 언어 설정
            const consoleDefaultLanguage = settings?.General?.consoleDefaultLanguage?.value;
            if (consoleDefaultLanguage) {
                const isUpdatedLocale = !!localStorage.getItem(`i18nLng`);
                updateGeneralSettings({ consoleDefaultLanguage });

                if (!isUpdatedLocale) {
                    await i18n.changeLanguage(consoleDefaultLanguage).then(() => {
                        updateLocaleLanguage(consoleDefaultLanguage);
                    });
                }
            }

            if (isCustomMenu) {
                const setId = location.pathname.split("/customMenuSets/")?.[1]?.split("/")?.[0];
                await dispatch(loadCustomMenus({ isLibrary: isCms, setId }));
            } else await redirectDefaultMenus(settings);
        } catch (e) {
            console.error(e);
        }
    }, [stage, project]);

    useEffect(() => {
        if (!customMenus.data) return;
        if (customMenus.data && customMenus.projectId !== project.id) return;
        if (settings?.project?.MenuName?.selectMenu?.value !== "custom") {
            dispatch(resetCustomMenus());
            return;
        }
        redirectCustomMenus();
    }, [customMenus]);

    useEffect(() => {
        if (!stage || !project) return;

        makeMenus();
    }, [stage, project]);

    if (!global.isAgreementRequired && customMenus?.error) {
        return <AccessDenied />;
    }
    return (
        <>
            {siteError ? (
                <TokenExpiredModal
                    error={{ type: "NO_SITE_ACCESS" }}
                    onConfirm={() => {
                        setSiteError(false);
                        history.push(`${menuService.paths.spaces()}`);
                    }}
                />
            ) : (
                <EntryLoader />
            )}
        </>
    );
}

export default EntryConsole;
